import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CreditCard,
  Receipt,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  Download,
  Zap,
  TrendingUp,
  Rocket,
  Briefcase,
  Mic,
  Award,
  Calendar,
  DollarSign,
  Loader2,
  FileText,
  ExternalLink,
  RefreshCw
} from "lucide-react";

const PLAN_ICONS = {
  basic: Zap,
  growth_strategic: TrendingUp,
  growth_optimization: TrendingUp,
  growth_scale: Rocket,
  projects: Briefcase
};

const STATUS_STYLES = {
  paid: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/20", label: "Paid" },
  completed: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/20", label: "Completed" },
  pending: { icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/20", label: "Pending" },
  initiated: { icon: Clock, color: "text-blue-400", bg: "bg-blue-500/20", label: "Processing" },
  expired: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/20", label: "Expired" },
  failed: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/20", label: "Failed" }
};

export function SubscriptionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [products, setProducts] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, transRes, prodRes] = await Promise.all([
        authFetch(`${API}/payments/my-subscription`),
        authFetch(`${API}/payments/my-transactions`),
        authFetch(`${API}/payments/products`)
      ]);

      if (subRes.ok) setSubscription(await subRes.json());
      if (transRes.ok) setTransactions(await transRes.json());
      if (prodRes.ok) setProducts(await prodRes.json());
    } catch (_error) {
      console.error("Error fetching data:", _error);
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getProductName = (productId) => {
    if (!products) return productId;
    const allProducts = { ...products.subscriptions, ...products.one_time };
    return allProducts[productId]?.name || productId;
  };

  const downloadReceipt = (transaction) => {
    const receipt = `
RECEIPT
===============================
My Biz Consulting
Folcroft, PA

Date: ${formatDate(transaction.created_at)}
Transaction ID: ${transaction.session_id}

Product: ${transaction.product_name}
Amount: $${transaction.amount?.toFixed(2)} ${transaction.currency?.toUpperCase()}
Status: ${transaction.payment_status?.toUpperCase()}

${transaction.paid_at ? `Paid: ${formatDate(transaction.paid_at)}` : ""}

Thank you for your business!
===============================
    `.trim();

    const blob = new Blob([receipt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `receipt_${transaction.session_id?.slice(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Receipt downloaded");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  const PlanIcon = subscription?.subscription_tier ? PLAN_ICONS[subscription.subscription_tier] : CreditCard;
  const successfulTransactions = transactions.filter(t => t.payment_status === "paid" || t.status === "completed");
  const pendingTransactions = transactions.filter(t => t.payment_status === "pending" || t.status === "initiated");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              className="text-white/70 hover:text-white"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={fetchData}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">My Subscription & Payments</h1>

        {/* Current Subscription */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-400" />
            Current Plan
          </h2>
          
          {subscription?.subscription_tier ? (
            <Card className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-purple-500/30 flex items-center justify-center">
                    <PlanIcon className="w-7 h-7 text-purple-300" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white">{subscription.plan_name}</h3>
                    <p className="text-white/60">
                      {subscription.plan_price
                        ? (subscription.plan_interval === "engagement"
                          ? `$${Number(subscription.plan_price).toLocaleString()} / ${subscription.plan_duration || "engagement"}`
                          : `$${Number(subscription.plan_price).toLocaleString()}/month`)
                        : ""} • Started {formatDate(subscription.subscription_started_at)}
                    </p>
                  </div>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    Active
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="pt-6 text-center">
                <p className="text-white/60 mb-4">You don't have an active subscription</p>
                <Button
                  onClick={() => navigate("/pricing")}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  View Plans
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Purchased Products */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-pink-400" />
            Purchased Products
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* R.A.P.I.D. Access */}
            <Card className={`border-white/10 ${subscription?.rapid_access ? "bg-green-500/10 border-green-500/30" : "bg-white/5"}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${subscription?.rapid_access ? "bg-green-500/20" : "bg-white/10"}`}>
                    <Rocket className={`w-5 h-5 ${subscription?.rapid_access ? "text-green-400" : "text-white/40"}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-white">R.A.P.I.D. Program</h4>
                    <p className="text-sm text-white/60">5-session business acceleration</p>
                  </div>
                  {subscription?.rapid_access ? (
                    <Badge className="bg-green-500/20 text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Purchased
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-white/20 text-white/60">
                      $125
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Podcast Academy Access */}
            <Card className={`border-white/10 ${subscription?.podcast_access ? "bg-green-500/10 border-green-500/30" : "bg-white/5"}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${subscription?.podcast_access ? "bg-green-500/20" : "bg-white/10"}`}>
                    <Mic className={`w-5 h-5 ${subscription?.podcast_access ? "text-green-400" : "text-white/40"}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-white">Podcast Launch Academy</h4>
                    <p className="text-sm text-white/60">6-week podcast program</p>
                  </div>
                  {subscription?.podcast_access ? (
                    <Badge className="bg-green-500/20 text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Purchased
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-white/20 text-white/60">
                      $500
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {!subscription?.rapid_access && !subscription?.podcast_access && (
            <p className="text-center text-white/40 mt-4 text-sm">
              No one-time products purchased yet.{" "}
              <button onClick={() => navigate("/pricing")} className="text-purple-400 hover:underline">
                Browse products
              </button>
            </p>
          )}
        </section>

        {/* Pending Transactions */}
        {pendingTransactions.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              Pending Payments
            </h2>
            <div className="space-y-3">
              {pendingTransactions.map((tx) => {
                const statusStyle = STATUS_STYLES[tx.payment_status] || STATUS_STYLES.pending;
                const StatusIcon = statusStyle.icon;
                
                return (
                  <Card key={tx.session_id} className="bg-yellow-500/10 border-yellow-500/20">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${statusStyle.bg} flex items-center justify-center`}>
                          <StatusIcon className={`w-5 h-5 ${statusStyle.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white">{tx.product_name}</p>
                          <p className="text-sm text-white/60">{formatDate(tx.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-white">${tx.amount?.toFixed(2)}</p>
                          <Badge className={`${statusStyle.bg} ${statusStyle.color}`}>
                            {statusStyle.label}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* Payment History */}
        <section>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-blue-400" />
            Payment History
          </h2>

          {successfulTransactions.length > 0 ? (
            <div className="space-y-3">
              {successfulTransactions.map((tx) => {
                const statusStyle = STATUS_STYLES[tx.payment_status] || STATUS_STYLES.paid;
                const StatusIcon = statusStyle.icon;

                return (
                  <Card key={tx.session_id} className="bg-white/5 border-white/10 hover:border-white/20 transition-colors">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${statusStyle.bg} flex items-center justify-center`}>
                          <StatusIcon className={`w-5 h-5 ${statusStyle.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white">{tx.product_name}</p>
                          <div className="flex items-center gap-2 text-sm text-white/60">
                            <Calendar className="w-3 h-3" />
                            {formatDate(tx.paid_at || tx.created_at)}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-white">${tx.amount?.toFixed(2)}</p>
                          <p className="text-xs text-white/40 uppercase">{tx.currency}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-white/60 hover:text-white"
                          onClick={() => downloadReceipt(tx)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-8 text-center">
                <Receipt className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/60">No payment history yet</p>
                <Button
                  variant="link"
                  className="text-purple-400 mt-2"
                  onClick={() => navigate("/pricing")}
                >
                  Make your first purchase
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Summary Stats */}
        {successfulTransactions.length > 0 && (
          <section className="mt-8">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-white">{successfulTransactions.length}</p>
                    <p className="text-sm text-white/60">Total Payments</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-400">
                      ${successfulTransactions.reduce((sum, tx) => sum + (tx.amount || 0), 0).toFixed(2)}
                    </p>
                    <p className="text-sm text-white/60">Total Spent</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-400">
                      {(subscription?.rapid_access ? 1 : 0) + (subscription?.podcast_access ? 1 : 0) + (subscription?.subscription_tier ? 1 : 0)}
                    </p>
                    <p className="text-sm text-white/60">Active Products</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        )}
      </main>
    </div>
  );
}

export default SubscriptionPage;
